import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import type { components } from "@/api/types";
import { duration, prefersReducedMotion, toChartNumber } from "@/shared/lib";

type ChartPoint = components["schemas"]["ChartPoint"];

/**
 * Область под кривой — общий примитив для графика баланса и витринного графика
 * робота.
 *
 * Живёт в `shared`, а не в `widgets/equity-chart`, по причине из
 * `eslint.config.js`: виджет не имеет права импортировать другой виджет, а
 * второй экземпляр recharts-обёртки разошёлся бы с первым на первой же правке
 * тултипа.
 *
 * Грузится лениво у каждого вызывающего: recharts весит заметно больше
 * остального приложения, а в Telegram WebView на плохой сети это прямые
 * секунды до первого экрана.
 *
 * Движение (FE-13): ряд отрисовывается слева направо. Единственная анимация в
 * приложении, которую считает не браузер, а JS библиотеки, — своей у recharts
 * не отключить. Кривую она принимает только из своего короткого набора,
 * поэтому здесь `ease-out`, а не токен: ближайшее, что есть.
 */
export function AreaPlot({
  points,
  gradientId,
  stroke,
  formatLabel,
  formatValue,
}: {
  points: ChartPoint[];
  /** Уникальный id градиента: два графика на одном экране затёрли бы друг друга. */
  gradientId: string;
  stroke: string;
  formatLabel: (at: string) => string;
  /** Готовая подпись значения. Строка, а не число: точность теряться не должна. */
  formatValue: (raw: string) => string;
}) {
  // Число появляется только здесь — recharts не принимает строки. В подписях и
  // тултипе выводится исходная строка, иначе потеря точности вылезла бы в
  // интерфейс.
  const data = points.map((point) => ({
    at: point.at,
    value: toChartNumber(point.value),
    raw: point.value,
  }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity={0.28} />
            <stop offset="100%" stopColor={stroke} stopOpacity={0} />
          </linearGradient>
        </defs>
        {/* Обе оси скрыты, но обе нужны. X — чтобы в тултип приходил слот точки,
            а не её индекс в массиве: без `dataKey` recharts подставляет в
            `label` порядковый номер, и подпись остаётся пустой. Y — чтобы домен
            считался от данных, иначе recharts берёт его от нуля и колебания
            схлопываются в прямую линию. */}
        <XAxis dataKey="at" hide />
        <YAxis hide domain={["dataMin", "dataMax"]} />
        <Tooltip
          cursor={{ stroke: "var(--color-border-strong)" }}
          contentStyle={{
            background: "var(--color-background-elevated)",
            border: "1px solid var(--color-border-subtle)",
            borderRadius: 12,
            fontSize: 12,
          }}
          labelFormatter={(label) => (typeof label === "string" ? formatLabel(label) : "")}
          formatter={(_value, _name, item) => {
            const point: unknown = item.payload;
            const raw =
              point && typeof point === "object" && "raw" in point ? String(point.raw) : "0.00";
            return [formatValue(raw), ""];
          }}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={stroke}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          isAnimationActive={!prefersReducedMotion()}
          animationDuration={duration("slow")}
          animationEasing="ease-out"
          dot={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
